import { useCallback, useEffect, useState } from "react";
import { FibiLoading } from "../components/FibiLoading";
import { useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Ban, Copy, MapPin, MessageCircle, MessageSquareWarning, Pencil, Plus, Power, Receipt, RefreshCcw, ScrollText, Search, Settings2, Trash2 } from "lucide-react";
import { MikrotikLogDialog } from "../components/MikrotikLogDialog";
import { toast } from "sonner";
import { api, errMsg, fmtDate, rupiah, ruleCommissions } from "../lib/api";
import { paySaleWithMidtrans } from "../lib/midtrans";
import { bulanTagihan, fetchInvoiceNo, jatuhTempo10 } from "../lib/invoiceImage";
import { InvoiceDialog } from "../components/InvoiceDialog";
import { PppComplaintDialog } from "../components/PppComplaintDialog";
import { ChurnReasonDialog, churnLabel } from "../components/ChurnReasonDialog";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { AgentSearchSelect } from "../components/AgentSearchSelect";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import CommissionPicker from "../components/CommissionPicker";
import PinConfirm from "../components/PinConfirm";
import { DatePicker } from "../components/DatePicker";
import { MapPointPicker } from "../components/MapPointPicker";

const NAME_COLOR = (s) =>
  s.status === "online" ? "text-emerald-400"                       // hijau terang = online
  : s.status === "active" && s.expired_date ? "text-rose-500"      // merah terang = offline
  : "text-red-500";                                                // merah = belum perpanjang
const NAME_TITLE = (s) =>
  s.status === "online" ? "Online"
  : s.status === "active" && s.expired_date ? "Offline"
  : "Belum perpanjang";

const PPP_PROFILE_EMPTY = { name: "", local_address: "", remote_address: "", rate_limit: "", modal: 0, jual: 0, expired_days: 30, catatan: "", hide_from_agents: false };

export default function PppSecrets() {
  const { user: me } = useAuth();
  const [enablingAll, setEnablingAll] = useState(false);
  const [pulihkan, setPulihkan] = useState(false);
  const [pulihkanSemua, setPulihkanSemua] = useState(false);
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const { selected, router, setSelected } = useRouters();
  const [logOpen, setLogOpen] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [items, setItems] = useState(null);
  const [churnMap, setChurnMap] = useState({});
  const [churnTarget, setChurnTarget] = useState(null);
  const loadChurn = useCallback(() => { if (selected) api.get(`/routers/${selected}/ppp/alasan-berhenti`).then((r) => setChurnMap(r.data)).catch(() => {}); }, [selected]);
  useEffect(() => { loadChurn(); }, [loadChurn]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selNames, setSelNames] = useState(new Set());
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [pinRenameOpen, setPinRenameOpen] = useState(false);
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [bulkRenewOpen, setBulkRenewOpen] = useState(false);
  const [bulkRenew, setBulkRenew] = useState({ months: 1, payment_method: "tunai" });
  const [bulkRenewProg, setBulkRenewProg] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", password: "", profile: "default", price: 0, expired_date: "" });
  const [copySource, setCopySource] = useState(null);
  const [pinCopyOpen, setPinCopyOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("profiles") === "1") {
      setPkgForm(null);
      setPkgDelete(null);
      setPkgOpen(true);
      setSearchParams({}, { replace: true });
      toast.info("Menampilkan pengaturan profile WiFi Rumahan cabang ini");
    } else if (searchParams.get("add-secret") === "1") {
      setAddOpen(true);
      setSearchParams({}, { replace: true });
      toast.info("Silakan buat secret WiFi Rumahan baru untuk cabang ini");
    } else if (searchParams.get("secret")) {
      const rid = searchParams.get("router");
      if (rid && rid !== selected) setSelected(rid);
      setSearch(searchParams.get("secret"));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pkgForm, setPkgForm] = useState(null);
  const [savingPkg, setSavingPkg] = useState(false);
  const [pkgPinOpen, setPkgPinOpen] = useState(false);
  const [pkgDelete, setPkgDelete] = useState(null);
  const [renewItem, setRenewItem] = useState(null);
  const [renew, setRenew] = useState({ months: 1, price: 0, payment_method: "saldo", sold_by: "", commissions: [] });
  const [pwTarget, setPwTarget] = useState(null);
  const [complainItem, setComplainItem] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pinPwOpen, setPinPwOpen] = useState(false);
  const renewKurang = !isAdmin && Number(renew.price) * Number(renew.months) > (me?.balance || 0);
  const [setupItem, setSetupItem] = useState(null);
  const [setupForm, setSetupForm] = useState({ expired_date: "", status: "keep" });
  const [setupMapOpen, setSetupMapOpen] = useState(false);
  const [savingSetup, setSavingSetup] = useState(false);
  const [diag, setDiag] = useState(null); // { name, loading, data }

  const openDiag = async (s) => {
    setDiag({ name: s.name, loading: true, data: null });
    try {
      const { data } = await api.get(`/routers/${selected}/ppp/${encodeURIComponent(s.name)}/diagnosa`);
      setDiag({ name: s.name, loading: false, data });
    } catch (err) { toast.error(errMsg(err)); setDiag(null); }
  };

  const [redialing, setRedialing] = useState(false);
  const doPaksaDial = async (name) => {
    if (!window.confirm(`Paksa dial ulang ${name}? Sesi aktif diputus agar modem login ulang. Bila 60 detik belum online, sistem mengirim pesan ke agen/teknisi tertaut.`)) return;
    setRedialing(true);
    try { const { data } = await api.post(`/routers/${selected}/ppp/${encodeURIComponent(name)}/paksa-dial`); toast.success(data.note); setTimeout(() => openDiag({ name }), 8000); }
    catch (e) { toast.error(errMsg(e)); } finally { setRedialing(false); }
  };
  const doAktifkan = async (name) => {
    try {
      const { data } = await api.post(`/routers/${selected}/ppp/${encodeURIComponent(name)}/aktifkan`);
      toast.success(`MikroTik: ${data.note}`);
      load();
      openDiag({ name });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doSetup = async (e, pin = "") => {
    e?.preventDefault?.();
    if (!setupItem) return;
    if (setupForm.status === "nonactive" && !pin) { setPinSetupOpen(true); return; }
    setSavingSetup(true);
    try {
      const { data } = await api.put(`/routers/${selected}/ppp/${encodeURIComponent(setupItem.name)}/setup`, {
        expired_date: setupForm.expired_date, status: setupForm.status,
        price: setupForm.price === "" || setupForm.price == null ? null : Number(setupForm.price),
        modal: setupForm.modal === "" || setupForm.modal == null ? null : Number(setupForm.modal),
        lat: setupForm.lat ? Number(setupForm.lat) : null, lng: setupForm.lng ? Number(setupForm.lng) : null,
        pin, reason: setupForm.reason || "",
      });
      toast.success(`Secret ${setupItem.name} disetup — expired ${fmtDate(data.expired_date)}, status ${setupForm.status === "keep" ? "tidak diubah" : setupForm.status === "active" ? "Active" : "Nonactive"}`);
      setSetupItem(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingSetup(false);
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSel, setBulkSel] = useState(new Set());
  const [bulkForm, setBulkForm] = useState({ expired_date: "", status: "keep" });
  const [savingBulk, setSavingBulk] = useState(false);
  const [bulkQ, setBulkQ] = useState("");

  const openBulk = () => {
    setBulkSel(new Set((items || []).filter((s) => !s.expired_date).map((s) => s.name)));
    setBulkForm({ expired_date: "", status: "keep" });
    setBulkQ("");
    setBulkOpen(true);
  };

  // lembar kerja PDF untuk teknisi: nama secret + jatuh tempo hasil setup masal
  const downloadBulkPdf = (names, expDate) => {
    const rows = (items || []).filter((s) => names.includes(s.name));
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Setup Masal PPP — ${router?.name || "Router"}`, 14, 16);
    doc.setFontSize(10);
    doc.text(`Jatuh tempo: ${fmtDate(expDate)}  •  Status: ${bulkForm.status === "keep" ? "tidak diubah" : bulkForm.status === "active" ? "Active" : "Nonactive"}  •  ${rows.length} secret`, 14, 23);
    autoTable(doc, {
      startY: 28,
      head: [["No", "Nama Secret", "Password", "Profile", "Jatuh Tempo"]],
      body: rows.map((s, i) => [i + 1, s.name, s.password, s.profile, fmtDate(expDate)]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [240, 78, 35] },
    });
    doc.save(`setup-masal-${(router?.name || "ppp").replace(/\s+/g, "-")}-${expDate}.pdf`);
  };

  const doBulk = async (e) => {
    e.preventDefault();
    if (!bulkSel.size) return;
    setSavingBulk(true);
    try {
      const { data } = await api.put(`/routers/${selected}/ppp/setup-bulk`, { names: [...bulkSel], ...bulkForm });
      toast.success(`Setup masal selesai — ${data.updated} secret disetup s/d ${fmtDate(data.expired_date)}${data.missing?.length ? ` (${data.missing.length} tidak ditemukan di router)` : ""}`, {
        action: { label: "Unduh PDF", onClick: () => downloadBulkPdf([...bulkSel], data.expired_date) },
      });
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingBulk(false);
    }
  };

  const load = useCallback(() => {
    if (!selected) return;
    setItems(null);
    api.get(`/routers/${selected}/ppp`).then((r) => setItems(r.data)).catch((e) => { setItems([]); toast.error(errMsg(e)); });
    api.get(`/routers/${selected}/profiles`).then((r) => setProfiles(r.data.ppp || [])).catch(() => {});
    api.get(`/routers/${selected}/ppp-profiles`).then((r) => setPackages(r.data)).catch(() => setPackages([]));
  }, [selected]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/agents").then((r) => setAgents(r.data)).catch(() => {}); }, []);

  const doCreate = async () => {
    try {
      const { data } = await api.post(`/routers/${selected}/ppp`, { ...form, price: Number(form.price) });
      toast.success(copySource ? `Secret ${form.name} dibuat (copy dari ${copySource})` : `Secret ${form.name} dibuat`);
      if (data?.warning) toast.warning(data.warning, { duration: 10000 });
      setAddOpen(false);
      setCopySource(null);
      setForm({ name: "", password: "", profile: "default", price: 0, expired_date: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (copySource) { setPinCopyOpen(true); return; }
    await doCreate();
  };

  const openCopy = (s) => {
    setForm({ name: "", password: s.password || "", profile: s.profile || "default", price: s.price || 0, expired_date: s.expired_date || "" });
    setCopySource(s.name);
    setAddOpen(true);
  };

  const [invoiceData, setInvoiceData] = useState(null);

  const openInvoice = async (s, overrideExp) => {
    const exp = overrideExp || s.expired_date;
    const no = await fetchInvoiceNo(s.name, exp);
    setInvoiceData({
      no, nama: s.customer || s.name, alamat: router?.name || "-",
      paket: s.profile || "WiFi Rumahan", bulan: bulanTagihan(exp),
      harga: s.price || 0, ppn: 0, total: s.price || 0,
      tanggal: new Date().toISOString(), jatuh_tempo: jatuhTempo10(exp),
    });
  };

  const doRenew = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/routers/${selected}/ppp/${encodeURIComponent(renewItem.name)}/perpanjang`, {
        months: Number(renew.months), price: Number(renew.price), payment_method: isAdmin ? renew.payment_method : "saldo",
        customer: renewItem.customer || renewItem.name, sold_by: renew.sold_by,
        commissions: renew.commissions.filter((c) => c.agent_code && c.amount > 0),
      });
      setRenewItem(null);
      if (renew.payment_method === "midtrans") paySaleWithMidtrans(data.sale, renewItem.name, load);
      else {
        toast.success(`Diperpanjang s/d ${fmtDate(data.expired_date)} — ${rupiah(data.sale.total)}`, {
          action: { label: "Invoice", onClick: () => openInvoice({ ...renewItem, price: data.sale.total }, data.expired_date) },
        });
        if (data.warning) toast.warning(data.warning, { duration: 10000 });
        if (data.router_note) toast.info(`MikroTik: ${data.router_note}`, { duration: 10000 });
        load();
      }
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/routers/${selected}/ppp/${encodeURIComponent(deleteTarget)}`); toast.success("Secret dihapus"); setDeleteTarget(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggleSel = (name) => setSelNames((prev) => { const n = new Set(prev); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  const downloadBackup = (text, filename) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    a.download = filename; a.click(); URL.revokeObjectURL(a.href);
  };
  const doBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post(`/routers/${selected}/ppp/bulk-delete`, { names: [...selNames] });
      downloadBackup(data.backup_text, data.filename);
      toast.success(`${data.removed} secret dihapus — backup nama & password diunduh (${data.filename})`);
      setBulkDelOpen(false); setSelNames(new Set()); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBulkDeleting(false); }
  };

  const doBulkRenew = async () => {
    const targets = (items || []).filter((s) => selNames.has(s.name));
    setBulkRenewProg({ done: 0, total: targets.length, ok: 0, fail: [] });
    for (const s of targets) {
      try {
        await api.post(`/routers/${selected}/ppp/${encodeURIComponent(s.name)}/perpanjang`, {
          months: Number(bulkRenew.months), price: Number(s.price || 0), payment_method: bulkRenew.payment_method,
          customer: s.customer || s.name, sold_by: "", commissions: [],
        });
        setBulkRenewProg((p) => ({ ...p, done: p.done + 1, ok: p.ok + 1 }));
      } catch (err) {
        setBulkRenewProg((p) => ({ ...p, done: p.done + 1, fail: [...p.fail, `${s.name}: ${errMsg(err)}`] }));
      }
    }
    setSelNames(new Set()); load();
  };

  const saveRename = async () => {
    try {
      await api.put(`/routers/${selected}/ppp/${encodeURIComponent(renameTarget.name)}/rename`, { new_name: renameValue.trim() });
      toast.success(`Nama ${renameTarget.name} diganti menjadi ${renameValue.trim()}`);
      setRenameTarget(null); setRenameValue(""); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const savePassword = async () => {
    try {
      await api.put(`/routers/${selected}/ppp/${encodeURIComponent(pwTarget.name)}/password`, { password: pwValue });
      toast.success(`Password ${pwTarget.name} berhasil diubah`);
      setPwTarget(null);
      setPwValue("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openPackages = () => { setPkgForm(null); setPkgDelete(null); setPkgOpen(true); };

  const saveProfile = async () => {
    setSavingPkg(true);
    try {
      const payload = { name: pkgForm.name.trim(), local_address: pkgForm.local_address, remote_address: pkgForm.remote_address, rate_limit: pkgForm.rate_limit, modal: Number(pkgForm.modal) || 0, jual: Number(pkgForm.jual) || 0, expired_days: Number(pkgForm.expired_days) || 30, catatan: pkgForm.catatan, hide_from_agents: !!pkgForm.hide_from_agents, apply_to_secrets: !!pkgForm.apply_to_secrets };
      if (pkgForm.editing) {
        const { data } = await api.put(`/routers/${selected}/ppp-profiles/${encodeURIComponent(pkgForm.editing)}`, payload);
        if (payload.apply_to_secrets) { toast.success(`Harga diterapkan ke ${data.applied || 0} pelanggan`); load(); }
      } else {
        await api.post(`/routers/${selected}/ppp-profiles`, payload);
      }
      toast.success("Profile WiFi Rumahan tersimpan");
      setPkgForm(null);
      const { data } = await api.get(`/routers/${selected}/ppp-profiles`);
      setPackages(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setSavingPkg(false); }
  };

  const doDeleteProfile = async () => {
    try {
      await api.delete(`/routers/${selected}/ppp-profiles/${encodeURIComponent(pkgDelete)}`);
      toast.success(`Profile ${pkgDelete} dihapus`);
      setPkgDelete(null);
      const { data } = await api.get(`/routers/${selected}/ppp-profiles`);
      setPackages(data);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const [onlyUnsetup, setOnlyUnsetup] = useState(false);
  const [onlyIsolir, setOnlyIsolir] = useState(false);
  const isolirCount = (items || []).filter((s) => s.isolir).length;
  const waRemind = (s) => {
    const digits = (s.name.match(/^0\d{8,13}/) || [])[0] || "";
    const phone = digits ? `62${digits.slice(1)}` : "";
    const text = `Halo Bapak/Ibu pelanggan WiFi Rumahan Fasnet Fiber (${s.name}).\nMasa aktif internet Anda sudah habis${s.expired_date ? ` pada ${fmtDate(s.expired_date)}` : ""}${s.price ? `, tagihan Rp ${Number(s.price).toLocaleString("id-ID")}` : ""}.\nSilakan bayar di ${window.location.origin}/portal (masukkan secret: ${s.name}) atau hubungi agen Fasnet terdekat. Internet pulih otomatis setelah lunas. Terima kasih 🙏`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
    if (!phone) toast.info("Nomor HP tidak terbaca dari nama secret — pilih kontak di WhatsApp secara manual");
  };
  const unsetupCount = (items || []).filter((s) => !s.expired_date).length;
  const agenSetupOpen = !isAdmin && new Date().toISOString().slice(0, 7) === "2026-10";
  const canSetup = isAdmin || agenSetupOpen;
  const filtered = (items || []).filter((s) => (!search || s.name.toLowerCase().includes(search.toLowerCase())) && (!onlyUnsetup || !s.expired_date) && (!onlyIsolir || s.isolir));

  return (
    <div className="space-y-6" data-testid="ppp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">WiFi Rumahan</h1>
          <p className="text-sm text-muted-foreground mt-1">PPP Secrets & perpanjangan pelanggan di {router?.name || "router"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-refresh-ppp"><RefreshCcw className="w-4 h-4 mr-1.5" /> Muat Ulang</Button>
          {isAdmin && (
            <Button variant="outline" onClick={openPackages} data-testid="btn-open-ppp-packages">
              <Settings2 className="w-4 h-4 mr-1.5" /> Pengaturan Paket
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" className="text-sky-400 border-sky-500/40 hover:bg-sky-500/10" onClick={openBulk} data-testid="btn-setup-bulk">
              <Settings2 className="w-4 h-4 mr-1.5" /> Setup Masal
            </Button>
          )}
          {me?.role === "admin" && (
            <Button variant="outline" className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10" disabled={enablingAll} data-testid="btn-enable-all-ppp"
              onClick={async () => {
                if (!window.confirm("Aktifkan (enable) SEMUA secret PPP yang sedang disabled di router ini?")) return;
                setEnablingAll(true);
                try { const { data } = await api.post(`/routers/${selected}/ppp/enable-all`); toast.success(`${data.enabled} secret diaktifkan kembali`); load(); }
                catch (e) { toast.error(errMsg(e)); } finally { setEnablingAll(false); }
              }}>
              <Power className="w-4 h-4 mr-1.5" /> {enablingAll ? "Mengaktifkan..." : "Aktifkan Semua Secret"}
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" className="text-sky-500 border-sky-500/40 hover:bg-sky-500/10" disabled={pulihkan} title="Aktifkan kembali secret yang disabled padahal belum expired & hapus script penyebabnya" data-testid="btn-pulihkan-ppp"
              onClick={async () => {
                setPulihkan(true);
                try { const { data } = await api.post(`/routers/${selected}/ppp-pulihkan`); toast.success(data.note); load(); }
                catch (e) { toast.error(errMsg(e)); } finally { setPulihkan(false); }
              }}>
              <RefreshCcw className={`w-4 h-4 mr-1.5 ${pulihkan ? "animate-spin" : ""}`} /> Pulihkan Belum Expired
            </Button>
          )}
          {me?.role === "admin" && (
            <Button variant="outline" className="text-sky-500 border-sky-500/40 hover:bg-sky-500/10" disabled={pulihkanSemua} title="Jalankan Pulihkan Belum Expired ke SEMUA cabang — hasil dikirim ke chat admin" data-testid="btn-pulihkan-ppp-semua"
              onClick={async () => {
                if (!window.confirm("Pulihkan secret belum-expired yang disabled di SEMUA cabang? Hasil dikirim ke chat admin.")) return;
                setPulihkanSemua(true);
                try { const { data } = await api.post("/routers/ppp-pulihkan-semua"); toast.success(data.note); }
                catch (e) { toast.error(errMsg(e)); } finally { setPulihkanSemua(false); }
              }}>
              <RefreshCcw className={`w-4 h-4 mr-1.5 ${pulihkanSemua ? "animate-spin" : ""}`} /> Pulihkan Semua Cabang
            </Button>
          )}
          <Button onClick={() => setAddOpen(true)} data-testid="btn-add-ppp"><Plus className="w-4 h-4 mr-1.5" /> Buat Secret Baru</Button>
        </div>
      </div>

      <Card className="bg-card/60 border-border">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cari nama pelanggan..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="input-ppp-search" />
          </div>
          <Button type="button" variant={onlyUnsetup ? "default" : "outline"} size="sm" className={onlyUnsetup ? "bg-amber-500 hover:bg-amber-600 text-black" : "text-amber-500 border-amber-500/40 hover:bg-amber-500/10"} onClick={() => setOnlyUnsetup((v) => !v)} data-testid="btn-filter-unsetup">
            Belum di-setup ({unsetupCount})
          </Button>
          <Button type="button" variant={onlyIsolir ? "default" : "outline"} size="sm" className={onlyIsolir ? "bg-rose-500 hover:bg-rose-600 text-white" : "text-rose-400 border-rose-500/40 hover:bg-rose-500/10"} onClick={() => setOnlyIsolir((v) => !v)} title="Pelanggan yang internetnya diblok: jatuh tempo lewat / profile isolir / disabled" data-testid="btn-filter-isolir">
            <Ban className="w-3.5 h-3.5 mr-1.5" /> Expired/Nonaktif ({isolirCount})
          </Button>
          {["admin", "admin_fasnet"].includes(me?.role) && selected && (
            <Button type="button" variant="outline" size="sm" className="text-amber-500 border-amber-500/40 hover:bg-amber-500/10" onClick={() => setLogOpen(true)} data-testid="btn-ppp-mikrotik-log">
              <ScrollText className="w-3.5 h-3.5 mr-1.5" /> Log MikroTik
            </Button>
          )}
          {["admin", "admin_fasnet"].includes(me?.role) && (
            <Button type="button" variant="outline" size="sm" className="text-sky-400 border-sky-500/40 hover:bg-sky-500/10" disabled={broadcasting} data-testid="btn-ppp-broadcast-offline"
              onClick={async () => {
                if (!window.confirm("Kirim pengumuman ke agen & teknisi tertaut di SEMUA cabang: daftar pelanggan yang hari ini sudah diperpanjang tapi masih offline + solusi restart modem/ONT?")) return;
                setBroadcasting(true);
                try { const { data } = await api.post("/ppp/renew-offline/broadcast", {}); toast.success(`${data.offline} pelanggan offline dari ${data.renewed} perpanjangan hari ini — pengumuman terkirim ke ${data.branches.length} cabang${data.unreachable.length ? ` (router tidak terjangkau: ${data.unreachable.join(", ")})` : ""}`); }
                catch (e) { toast.error(errMsg(e)); } finally { setBroadcasting(false); }
              }}>
              <MessageSquareWarning className="w-3.5 h-3.5 mr-1.5" /> {broadcasting ? "Mengirim…" : "Umumkan Perpanjang Offline"}
            </Button>
          )}
          <MikrotikLogDialog router={logOpen && selected ? { id: selected, name: router?.name || "" } : null} onClose={() => setLogOpen(false)} />
          {me?.role === "admin" && selNames.size > 0 && (
            <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setBulkRenew({ months: 1, payment_method: "tunai" }); setBulkRenewProg(null); setBulkRenewOpen(true); }} data-testid="btn-bulk-renew-ppp">
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Perpanjang {selNames.size} Terpilih
            </Button>
          )}
          {me?.role === "admin" && selNames.size > 0 && (
            <Button type="button" variant="destructive" size="sm" onClick={() => setBulkDelOpen(true)} data-testid="btn-bulk-delete-ppp">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Hapus {selNames.size} Secret Terpilih
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <Table className="min-w-[760px]" data-testid="ppp-table">
          <TableHeader>
            <TableRow>
              {me?.role === "admin" && <TableHead className="w-8"><Checkbox checked={filtered.length > 0 && filtered.every((s) => selNames.has(s.name))} onCheckedChange={(c) => setSelNames(c ? new Set([...selNames, ...filtered.map((s) => s.name)]) : new Set([...selNames].filter((n) => !filtered.some((s) => s.name === n))))} title="Pilih semua (yang tampil)" data-testid="ppp-select-all" /></TableHead>}
              <TableHead>#</TableHead><TableHead>Nama</TableHead><TableHead>Password</TableHead>
              <TableHead>Profile</TableHead><TableHead>Expired</TableHead><TableHead>Harga</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null && <TableRow><TableCell colSpan={8} className="p-0"><FibiLoading /></TableCell></TableRow>}
            {items !== null && !filtered.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="ppp-empty">Tidak ada PPP secret</TableCell></TableRow>}
            {filtered.map((s, i) => (
              <TableRow key={s.name} className={selNames.has(s.name) ? "bg-rose-500/5" : ""} data-testid={`ppp-row-${s.name}`}>
                {me?.role === "admin" && <TableCell><Checkbox checked={selNames.has(s.name)} onCheckedChange={() => toggleSel(s.name)} data-testid={`ppp-select-${s.name}`} /></TableCell>}
                <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                <TableCell className={`font-semibold ${NAME_COLOR(s)}`} title={NAME_TITLE(s)} data-testid={`ppp-status-${s.name}`}>
                  <span className="inline-flex items-center gap-1.5 cursor-pointer hover:underline" onClick={() => openDiag(s)} title="Klik untuk diagnosa kenapa tidak online">
                    {s.name}
                    {s.disabled && <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-500/15 text-rose-400 border border-rose-500/30" title={s.disabled_info ? `Dinonaktifkan: ${s.disabled_info.replace(/@/g, " · ")}` : "Dinonaktifkan manual (via Winbox/sebelum pencatatan)"} data-testid={`ppp-disabled-${s.name}`}>DISABLED</span>}
                    {s.disabled && s.disabled_info && <span className="text-[10px] text-rose-300/80 truncate max-w-[220px]" data-testid={`ppp-disabled-info-${s.name}`}>{s.disabled_info.replace(/@/g, " · ")}</span>}
                    {String(s.profile || "").startsWith("fasnet-isolir-") && <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-500/15 text-amber-400 border border-amber-500/30" title="Internet diblok karena jatuh tempo — modem tetap tersambung, perpanjang untuk memulihkan" data-testid={`ppp-isolir-${s.name}`}>ISOLIR</span>}
                    {s.isolir && <button type="button" onClick={(e) => { e.stopPropagation(); waRemind(s); }} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25" title="Kirim pengingat bayar via WhatsApp" data-testid={`ppp-wa-remind-${s.name}`}><MessageCircle className="w-3 h-3" /> Ingatkan WA</button>}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-muted-foreground">{s.password}</TableCell>
                <TableCell><span className="px-2 py-0.5 rounded text-[11px] font-mono bg-sky-500/10 text-sky-400 border border-sky-500/20">{s.profile}</span></TableCell>
                <TableCell className={`font-mono text-sm ${s.status === "expired" ? "text-rose-400" : ""}`} data-testid={`ppp-expired-${s.name}`}>
                  {s.expired_date ? fmtDate(s.expired_date) : (
                    <span className="inline-flex items-center gap-1.5">
                      belum di-setup
                      {canSetup && (
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-sky-400" title={isAdmin ? "Setup manual secret PPP" : "Set jatuh tempo (1x, migrasi Oktober 2026)"} onClick={() => { setSetupItem(s); setSetupForm({ expired_date: "", status: "keep", lat: s.lat ?? "", lng: s.lng ?? "" }); }} data-testid={`btn-setup-ppp-${s.name}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      )}
                    </span>
                  )}
                </TableCell>
                <TableCell className="font-mono">
                  <span className="inline-flex items-center gap-1.5">
                    {s.price ? rupiah(s.price) : "-"}
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-sky-400" title="Ubah harga / setup secret" onClick={() => { setSetupItem(s); setSetupForm({ expired_date: s.expired_date || "", status: "keep", price: s.price ?? "", modal: s.modal ?? "", lat: s.lat ?? "", lng: s.lng ?? "" }); }} data-testid={`btn-edit-price-ppp-${s.name}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10" onClick={() => openInvoice(s)} data-testid={`btn-invoice-${s.name}`}>
                    <Receipt className="w-3.5 h-3.5 mr-1" /> Invoice
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setPwTarget(s); setPwValue(""); }} data-testid={`btn-passwd-${s.name}`}>
                    Password
                  </Button>
                  {me?.is_owner && (
                    <Button size="sm" variant="outline" className="text-sky-400 border-sky-500/40 hover:bg-sky-500/10" onClick={() => { setRenameTarget(s); setRenameValue(s.name); }} data-testid={`btn-rename-${s.name}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Nama
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setRenewItem(s); const pkgModal = packages.find((p) => p.profile === s.profile)?.modal; setRenew({ months: 1, price: isAdmin ? (s.price || 0) : (s.modal || pkgModal || s.price || 0), payment_method: "saldo", sold_by: "", commissions: [] }); }} data-testid={`btn-renew-${s.name}`}>
                    + Perpanjang
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/40 hover:bg-rose-500/10" title="Lapor masalah pelanggan ke admin & upline" onClick={() => setComplainItem(s)} data-testid={`btn-complain-${s.name}`}>
                    <MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Komplain
                  </Button>
                  {s.status === "expired" && (
                    <Button size="sm" variant={churnMap[s.name] ? "secondary" : "outline"} className={churnMap[s.name] ? "text-rose-500" : "text-muted-foreground"} title={churnMap[s.name] ? `Alasan: ${churnLabel(churnMap[s.name].reason)}` : "Catat alasan pelanggan berhenti"} onClick={() => setChurnTarget({ routerId: selected, name: s.name, current: churnMap[s.name] || null })} data-testid={`btn-churn-${s.name}`}>
                      {churnMap[s.name] ? churnLabel(churnMap[s.name].reason) : "Alasan Berhenti"}
                    </Button>
                  )}
                  {isAdmin && (
                    <>
                      <Button size="icon" variant="ghost" className="text-sky-400" title="Copy Secret" onClick={() => openCopy(s)} data-testid={`btn-copy-ppp-${s.name}`}><Copy className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => setDeleteTarget(s.name)} data-testid={`btn-delete-ppp-${s.name}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {churnTarget && <ChurnReasonDialog target={churnTarget} onClose={() => setChurnTarget(null)} onDone={loadChurn} />}

      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setCopySource(null); }}>
        <DialogContent data-testid="dialog-add-ppp">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{copySource && <Copy className="w-5 h-5 text-sky-400" />}{copySource ? `Copy Secret: ${copySource}` : "Buat Secret Baru"}</DialogTitle></DialogHeader>
          {copySource && <p className="text-xs text-muted-foreground">Data terisi otomatis dari <span className="font-mono font-semibold text-foreground">{copySource}</span> — isi username baru lalu simpan dengan konfirmasi PIN.</p>}
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Username (PPPoE)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="0812xxx_Nama" data-testid="input-ppp-name" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="input-ppp-password" /></div>
              <div className="space-y-1.5"><Label>Profile</Label>
                <select value={form.profile} onChange={(e) => { const v = e.target.value; const pk = packages.find((x) => x.profile === v); setForm({ ...form, profile: v, ...(isAdmin && pk ? { price: pk.jual ?? pk.harga } : {}) }); }} data-testid="select-ppp-profile"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                  {(profiles.length ? profiles : ["default"]).map((p) => <option key={p} value={p} data-testid={`ppp-profile-opt-${p}`}>{p}</option>)}
                </select>
              </div>
              {isAdmin ? (
                <>
                  <div className="space-y-1.5"><Label>Harga/Bulan (Rp)</Label><Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="input-ppp-price" /></div>
                  <div className="space-y-1.5"><Label>Tanggal Expired</Label><DatePicker value={form.expired_date} onChange={(v) => setForm({ ...form, expired_date: v })} testid="input-ppp-expired" /></div>
                </>
              ) : (
                <div className="space-y-1.5 col-span-2">
                  <Label>Harga & Expired</Label>
                  <div className="rounded-lg border border-border bg-accent/50 px-3 py-2 text-xs font-mono" data-testid="ppp-package-info">
                    {(() => { const pk = packages.find((x) => x.profile === form.profile); return pk ? `${rupiah(pk.jual ?? pk.harga)}/bulan • expired otomatis +${pk.expired_days ?? 30} hari (diatur admin)` : "Profile ini belum punya paket harga — minta Admin Fasnet / Super Admin mengatur di Pengaturan Paket."; })()}
                  </div>
                </div>
              )}
            </div>
            <Button type="submit" className="w-full" data-testid="btn-save-ppp">{copySource ? "Simpan Copy Secret (PIN)" : "Simpan Secret"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <PinConfirm
        open={pinCopyOpen}
        onOpenChange={setPinCopyOpen}
        title={`Copy Secret ${copySource}?`}
        description={`Secret baru "${form.name}" akan dibuat di router dengan data dari ${copySource}. Masukkan PIN untuk melanjutkan.`}
        confirmLabel="Ya, Simpan Copy"
        onConfirm={doCreate}
        testid="pin-copy-ppp"
      />

      <Dialog open={!!diag} onOpenChange={(o) => !o && setDiag(null)}>
        <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-2xl" data-testid="dialog-diag-ppp">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base sm:text-lg pr-6 break-all"><Search className="w-5 h-5 text-sky-400 shrink-0" /> <span>Diagnosa — <span className="font-mono">{diag?.name}</span></span></DialogTitle></DialogHeader>
          {diag?.loading ? <p className="text-sm text-muted-foreground animate-pulse">Membaca data dari MikroTik...</p> : diag?.data && (
            <div className="space-y-3 text-sm">
              {diag.data.verdict && (
                <div className={`rounded-xl border p-3 sm:p-4 ${diag.data.verdict.level === "error" ? "border-rose-500/40 bg-rose-500/10" : diag.data.verdict.level === "ok" ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="diag-verdict">
                  <div className={`font-bold text-sm sm:text-base leading-snug ${diag.data.verdict.level === "error" ? "text-rose-700 dark:text-rose-300" : diag.data.verdict.level === "ok" ? "text-emerald-700 dark:text-emerald-300" : "text-amber-800 dark:text-amber-200"}`}>{diag.data.verdict.title}</div>
                  <div className="text-foreground/90 mt-1 text-xs sm:text-sm">{diag.data.verdict.action}</div>
                </div>
              )}
              {diag.data.secret && (
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-[11px] sm:text-xs text-muted-foreground border border-border rounded-lg p-2.5 sm:p-3">
                  <span>Status</span><span className={`text-right ${diag.data.active?.length ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>{diag.data.active?.length ? "online" : "offline"}{diag.data.secret.disabled === "true" ? " · DISABLED" : ""}</span>
                  <span>Expired</span><span className="text-foreground text-right">{diag.data.meta?.expired_date ? fmtDate(diag.data.meta.expired_date) : "-"}</span>
                  <span>Logout terakhir</span><span className="text-foreground text-right">{diag.data.secret["last-logged-out"] || "-"}</span>
                  <span>Alasan putus</span><span className="text-foreground text-right break-all">{diag.data.secret["last-disconnect-reason"] || "-"}</span>
                </div>
              )}
              <details className="text-xs" data-testid="diag-detail-toggle">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none">Detail teknis ({diag.data.findings.length})</summary>
                <div className="mt-2 space-y-2">
              <div className="space-y-2" data-testid="diag-findings">
                {diag.data.findings.map((f, i) => (
                  <div key={i} className={`rounded-lg border px-3 py-2 ${f.level === "error" ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300" : f.level === "ok" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200"}`}>{f.text}</div>
                ))}
              </div>
              {diag.data.secret && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground border border-border rounded-lg p-3">
                  <span>Profile</span><span className="text-foreground">{diag.data.secret.profile || "-"}</span>
                  <span>Service</span><span className="text-foreground">{diag.data.secret.service || "any"}</span>
                  <span>Remote Address</span><span className="text-foreground">{diag.data.secret["remote-address"] || diag.data.profile?.["remote-address"] || "-"}</span>
                  <span>Last Caller ID</span><span className="text-foreground">{diag.data.secret["last-caller-id"] || "-"}</span>
                </div>
              )}
              {diag.data.logs?.length > 0 && (
                <div className="border border-border rounded-lg p-3 max-h-40 overflow-y-auto font-mono text-[11px] space-y-0.5" data-testid="diag-logs">
                  <div className="text-muted-foreground mb-1">Log MikroTik terkait secret ini:</div>
                  {diag.data.logs.map((l, i) => <div key={i}><span className="text-sky-600 dark:text-sky-400">{l.time}</span> <span className="text-muted-foreground">{l.topics}</span> {l.message}</div>)}
                </div>
              )}
                </div>
              </details>
              <div className="grid grid-cols-2 sm:flex gap-2">
                <Button variant="outline" size="sm" className="sm:flex-1" onClick={() => openDiag({ name: diag.name })} data-testid="diag-refresh"><RefreshCcw className="w-4 h-4 mr-1.5" /> Cek Ulang</Button>
                <Button variant="outline" size="sm" className="sm:flex-1 border-amber-500/40 text-amber-400 hover:bg-amber-500/10" disabled={redialing} onClick={() => doPaksaDial(diag.name)} data-testid="diag-paksa-dial"><RefreshCcw className={`w-4 h-4 mr-1.5 ${redialing ? "animate-spin" : ""}`} /> Paksa Dial</Button>
                {diag.data.secret && (isAdmin || (diag.data.meta?.expired_date && diag.data.meta.expired_date >= new Date().toISOString().slice(0, 10))) && (
                  <Button size="sm" className="col-span-2 sm:flex-1" onClick={() => doAktifkan(diag.name)} title={isAdmin ? "" : "Aktifkan secret & putus sesi — masa berlaku tidak berubah"} data-testid="diag-aktifkan"><Power className="w-4 h-4 mr-1.5" /> Aktifkan & Putus Sesi</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!setupItem} onOpenChange={(o) => !o && setSetupItem(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-setup-ppp">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-sky-400" /> {isAdmin ? "Informasi Secret — Setup Manual" : "Set Jatuh Tempo Pelanggan"}</DialogTitle></DialogHeader>
          {!isAdmin && (
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-xs space-y-1" data-testid="setup-ppp-agent-warning">
              <p className="font-semibold text-rose-400">PERINGATAN — pastikan tanggal jatuh tempo BENAR sesuai data pelanggan.</p>
              <p className="text-foreground/90">Pengaturan ini hanya bisa dilakukan <b>1 kali</b> per pelanggan dan hanya selama <b>Oktober 2026</b> (perpindahan dari sistem lama). Jatuh tempo tidak boleh melebihi <b>30 Oktober 2026</b>. Segala bentuk kecurangan akan dikenakan sanksi <b>BLOKIR AKUN</b>.</p>
            </div>
          )}
          <form onSubmit={doSetup} className="space-y-3">
            <div className="space-y-1.5"><Label>Nama Secret</Label><Input value={setupItem?.name || ""} disabled className="font-mono" data-testid="setup-ppp-name" /></div>
            <div className="space-y-1.5"><Label>Password Secret</Label><Input value={setupItem?.password || ""} disabled className="font-mono" data-testid="setup-ppp-password" /></div>
            <div className="space-y-1.5"><Label>Nama Profile</Label><Input value={setupItem?.profile || ""} disabled className="font-mono" data-testid="setup-ppp-profile" /></div>
            <div className="space-y-1.5"><Label>Expired Date</Label><DatePicker max={isAdmin ? undefined : "2026-10-30"} value={setupForm.expired_date} onChange={(v) => setSetupForm({ ...setupForm, expired_date: v })} testid="setup-ppp-expired" /></div>
            <div className="space-y-1.5"><Label>Titik Rumah Pelanggan</Label>
              <Button type="button" variant="outline" className="w-full justify-start text-sky-500 border-sky-500/40 hover:bg-sky-500/10" onClick={() => setSetupMapOpen(true)} data-testid="setup-ppp-map">
                <MapPin className="w-4 h-4 mr-2" /> {setupForm.lat ? `${Number(setupForm.lat).toFixed(5)}, ${Number(setupForm.lng).toFixed(5)} — geser ulang` : "Pilih titik di peta (opsional)"}
              </Button>
              <MapPointPicker open={setupMapOpen} lat={setupForm.lat ?? ""} lng={setupForm.lng ?? ""} onClose={() => setSetupMapOpen(false)} onPick={(la, ln) => setSetupForm((f) => ({ ...f, lat: la, lng: ln }))} />
            </div>
            {isAdmin && (<>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Harga Jual (Rp)</Label><Input type="number" min="0" placeholder="kosong = tidak diubah" value={setupForm.price ?? ""} onChange={(e) => setSetupForm({ ...setupForm, price: e.target.value })} data-testid="setup-ppp-price" /></div>
              <div className="space-y-1.5"><Label>Harga Modal (Rp)</Label><Input type="number" min="0" placeholder="kosong = tidak diubah" value={setupForm.modal ?? ""} onChange={(e) => setSetupForm({ ...setupForm, modal: e.target.value })} data-testid="setup-ppp-modal" /></div>
            </div>
            <div className="space-y-1.5"><Label>Status Active</Label>
              <Select value={setupForm.status} onValueChange={(v) => setSetupForm({ ...setupForm, status: v })}>
                <SelectTrigger data-testid="setup-ppp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep" data-testid="setup-status-keep">Jangan ubah (hanya set jatuh tempo)</SelectItem>
                  <SelectItem value="active" data-testid="setup-status-active">Active</SelectItem>
                  <SelectItem value="nonactive" data-testid="setup-status-nonactive">Nonactive</SelectItem>
                </SelectContent>
              </Select>
              {setupForm.status === "nonactive" && (
                <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-2 space-y-1.5" data-testid="setup-nonactive-warning">
                  <div className="text-xs text-rose-400 font-semibold">⚠ Pelanggan akan OFFLINE walau masa aktif belum habis. Wajib alasan + PIN.</div>
                  <Input value={setupForm.reason || ""} onChange={(e) => setSetupForm({ ...setupForm, reason: e.target.value })} placeholder="Alasan menonaktifkan (min. 5 huruf), mis. berhenti berlangganan" maxLength={60} data-testid="setup-nonactive-reason" />
                </div>
              )}
            </div>
            </>)}
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={() => setSetupItem(null)} data-testid="setup-ppp-cancel">Cancel</Button>
              <Button type="submit" disabled={savingSetup || !setupForm.expired_date || (!isAdmin && setupForm.expired_date > "2026-10-30") || (setupForm.status === "nonactive" && (setupForm.reason || "").trim().length < 5)} className={setupForm.status === "nonactive" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""} data-testid="setup-ppp-save">{savingSetup ? "Menyimpan..." : setupForm.status === "nonactive" ? "Nonaktifkan (PIN)" : "Save"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="max-w-md" data-testid="dialog-setup-bulk">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-sky-400" /> Setup Masal Secret PPP</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Semua secret terpilih disetup dengan <span className="font-semibold text-foreground">jatuh tempo yang sama</span>. Secret "belum di-setup" otomatis tercentang.</p>
          <form onSubmit={doBulk} className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Cari nama secret..." value={bulkQ} onChange={(e) => setBulkQ(e.target.value)} data-testid="bulk-search-input" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button type="button" size="sm" variant="outline" onClick={() => setBulkSel(new Set((items || []).map((s) => s.name)))} data-testid="bulk-select-all">Pilih Semua</Button>
              <Button type="button" size="sm" variant="outline" className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" onClick={() => setBulkSel(new Set((items || []).filter((s) => s.status === "online").map((s) => s.name)))} data-testid="bulk-select-active">Pilih yang Aktif (hijau)</Button>
              <Button type="button" size="sm" variant="outline" className="text-rose-400 border-rose-500/40 hover:bg-rose-500/10" onClick={() => setBulkSel(new Set((items || []).filter((s) => NAME_COLOR(s) === "text-red-500").map((s) => s.name)))} data-testid="bulk-select-red">Pilih yang Merah</Button>
              <Button type="button" size="sm" variant="outline" className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10" title="Centang semua secret belum di-setup & isi jatuh tempo = hari ini + 30 hari"
                onClick={() => { const d = new Date(); d.setDate(d.getDate() + 30); setBulkSel(new Set((items || []).filter((s) => !s.expired_date).map((s) => s.name))); setBulkForm({ ...bulkForm, expired_date: d.toISOString().slice(0, 10) }); }} data-testid="bulk-select-unset-30">Belum di-setup = hari ini + 30</Button>
            </div>
            <div className="max-h-56 overflow-y-auto border border-border rounded-xl divide-y divide-border/60" data-testid="bulk-list">
              {(items || []).filter((s) => s.name.toLowerCase().includes(bulkQ.trim().toLowerCase())).map((s) => (
                <label key={s.name} className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent/50 cursor-pointer">
                  <input type="checkbox" className="accent-blue-600 w-4 h-4"
                    checked={bulkSel.has(s.name)}
                    onChange={(e) => { const st = new Set(bulkSel); if (e.target.checked) st.add(s.name); else st.delete(s.name); setBulkSel(st); }}
                    data-testid={`bulk-check-${s.name}`} />
                  <span className="font-mono text-xs truncate flex-1">{s.name}</span>
                  {!s.expired_date && <span className="text-[9px] font-mono text-rose-400 shrink-0">belum di-setup</span>}
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Jatuh Tempo (sama untuk semua)</Label><DatePicker value={bulkForm.expired_date} onChange={(v) => setBulkForm({ ...bulkForm, expired_date: v })} testid="bulk-expired-input" /></div>
              <div className="space-y-1.5"><Label>Status Active</Label>
                <Select value={bulkForm.status} onValueChange={(v) => setBulkForm({ ...bulkForm, status: v })}>
                  <SelectTrigger data-testid="bulk-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep" data-testid="bulk-status-keep">Jangan ubah (hanya set jatuh tempo)</SelectItem>
                    <SelectItem value="active" data-testid="bulk-status-active">Active</SelectItem>
                    <SelectItem value="nonactive" data-testid="bulk-status-nonactive">Nonactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground" data-testid="bulk-selected-count">{bulkSel.size} dipilih</p>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel">Batal</Button>
                <Button type="submit" disabled={savingBulk || !bulkSel.size || !bulkForm.expired_date} data-testid="bulk-save">{savingBulk ? "Menyimpan..." : "Setup Masal"}</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkRenewOpen} onOpenChange={(o) => { if (!bulkRenewProg || bulkRenewProg.done >= bulkRenewProg.total) setBulkRenewOpen(o); }}>
        <DialogContent data-testid="dialog-bulk-renew-ppp">
          <DialogHeader><DialogTitle>Perpanjang {selNames.size || bulkRenewProg?.total || 0} Pelanggan Terpilih</DialogTitle></DialogHeader>
          {!bulkRenewProg ? (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Setiap pelanggan diperpanjang sesuai harga masing-masing. Total: <span className="font-mono text-foreground" data-testid="bulk-renew-total">{rupiah((items || []).filter((s) => selNames.has(s.name)).reduce((a, s) => a + (Number(s.price) || 0) * Number(bulkRenew.months), 0))}</span></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Durasi</Label>
                  <select value={String(bulkRenew.months)} onChange={(e) => setBulkRenew({ ...bulkRenew, months: Number(e.target.value) })} data-testid="select-bulk-renew-months"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="1">30 Hari</option><option value="3">3 Bulan</option><option value="6">6 Bulan</option><option value="12">12 Bulan</option>
                  </select>
                </div>
                <div className="space-y-1.5"><Label>Pembayaran</Label>
                  <select value={bulkRenew.payment_method} onChange={(e) => setBulkRenew({ ...bulkRenew, payment_method: e.target.value })} data-testid="select-bulk-renew-payment"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="tunai">Tunai</option><option value="transfer">Transfer</option>
                  </select>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border border-border text-xs font-mono divide-y divide-border" data-testid="bulk-renew-list">
                {(items || []).filter((s) => selNames.has(s.name)).map((s) => <div key={s.name} className="flex justify-between px-2 py-1"><span>{s.name}</span><span>{s.price ? rupiah(s.price) : <span className="text-amber-400">harga 0</span>}</span></div>)}
              </div>
              <Button type="button" className="w-full" onClick={doBulkRenew} data-testid="btn-bulk-renew-submit"><RefreshCcw className="w-4 h-4 mr-1.5" /> Perpanjang Sekarang</Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bulk-renew-progress">
              <div className="text-sm">Proses {bulkRenewProg.done}/{bulkRenewProg.total} — berhasil <span className="text-emerald-400 font-semibold">{bulkRenewProg.ok}</span>, gagal <span className="text-rose-400 font-semibold">{bulkRenewProg.fail.length}</span></div>
              <div className="h-2 rounded bg-accent overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${(bulkRenewProg.done / Math.max(1, bulkRenewProg.total)) * 100}%` }} /></div>
              {bulkRenewProg.fail.length > 0 && <div className="max-h-40 overflow-y-auto rounded-md border border-rose-500/30 bg-rose-500/5 text-xs p-2 space-y-1">{bulkRenewProg.fail.map((f) => <div key={f}>{f}</div>)}</div>}
              {bulkRenewProg.done >= bulkRenewProg.total && <Button type="button" variant="outline" className="w-full" onClick={() => setBulkRenewOpen(false)} data-testid="btn-bulk-renew-close">Tutup</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!renewItem} onOpenChange={() => setRenewItem(null)}>
        <DialogContent data-testid="dialog-renew-ppp">
          <DialogHeader><DialogTitle>Perpanjang: {renewItem?.name}</DialogTitle></DialogHeader>
          <form onSubmit={doRenew} className="space-y-3">
            <div className="text-sm text-muted-foreground">Expired saat ini: <span className="font-mono text-foreground">{renewItem?.expired_date ? fmtDate(renewItem.expired_date) : "belum di-setup"}</span>
              <span className="ml-2">→ Expired baru: <span className="font-mono text-emerald-400" data-testid="renew-new-expired">{(() => { const t = new Date(); t.setHours(0, 0, 0, 0); const cur = renewItem?.expired_date ? new Date(renewItem.expired_date) : null; const base = cur && cur > t ? cur : t; const d = new Date(base); d.setDate(d.getDate() + 30 * (renew.months || 1)); return fmtDate(d.toISOString().slice(0, 10)); })()}</span></span>
              {!renewItem?.expired_date && <div className="text-xs text-amber-400 mt-1" data-testid="renew-no-expired-hint">Belum ada tanggal expired — masa aktif dihitung 30 hari dari hari ini.</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Durasi</Label>
                {isAdmin ? (
                  <select value={String(renew.months)} onChange={(e) => setRenew({ ...renew, months: Number(e.target.value) })} data-testid="select-renew-months"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="1">30 Hari</option>
                    <option value="3">3 Bulan</option>
                    <option value="6">6 Bulan</option>
                    <option value="12">12 Bulan</option>
                  </select>
                ) : (
                  <div className="rounded-lg border border-border bg-accent/50 px-3 py-2 text-sm font-medium" data-testid="renew-months-locked">30 Hari</div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Harga/Bulan (Rp){!isAdmin && <span className="text-xs text-muted-foreground"> — sesuai pengaturan admin</span>}</Label>
                <Input type="number" min="0" value={renew.price} onChange={(e) => setRenew({ ...renew, price: e.target.value })} readOnly={!isAdmin} data-testid="input-renew-price" />
              </div>
              <div className="space-y-1.5 col-span-2"><Label>Pembayaran</Label>
                {isAdmin ? (
                  <select value={renew.payment_method} onChange={(e) => setRenew({ ...renew, payment_method: e.target.value })} data-testid="select-renew-payment"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="tunai">Tunai</option>
                    <option value="transfer">Transfer</option>
                    <option value="midtrans">Midtrans (QRIS/GoPay)</option>
                  </select>
                ) : (
                  <div className="rounded-lg border border-border bg-accent/50 px-3 py-2 text-sm font-medium" data-testid="renew-payment-locked">
                    Saldo
                    <span className="block text-[11px] font-normal text-muted-foreground">Perpanjangan PPP hanya bisa dibayar dengan saldo — pastikan saldo mencukupi.</span>
                  </div>
                )}
              </div>
              {isAdmin && (
                <div className="space-y-1.5 col-span-2"><Label>Agen Penjual (opsional — komisi jaringan terisi otomatis)</Label>
                  <AgentSearchSelect
                    options={agents.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))}
                    value={renew.sold_by} onChange={(v) => setRenew({ ...renew, sold_by: v, commissions: ruleCommissions(agents, v, Number(renew.price) * Number(renew.months)) })} placeholder="Tidak ada / langsung" testid="select-renew-agent" />
                </div>
              )}
            </div>
            <div className={`text-sm font-mono rounded-lg px-3 py-2 ${renewKurang ? "bg-rose-500/10 text-rose-500 border border-rose-500/20" : "bg-accent/50"}`} data-testid="renew-total">
              Total: {rupiah(Number(renew.price) * Number(renew.months))}{!isAdmin ? ` • Saldo: ${rupiah(me?.balance || 0)}` : ""}{renewKurang ? " — Saldo tidak cukup, top-up dulu" : ""}
            </div>
            {isAdmin && <CommissionPicker agents={agents} value={renew.commissions} onChange={(c) => setRenew({ ...renew, commissions: c })} testid="renew-commission" />}
            <Button type="submit" className="w-full" disabled={renewKurang} data-testid="btn-confirm-renew">Perpanjang & Catat Penjualan</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={pkgOpen} onOpenChange={setPkgOpen}>
        <DialogContent data-testid="dialog-ppp-packages">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-sky-400" /> Pengaturan Profile WiFi Rumahan</DialogTitle></DialogHeader>
          {pkgForm === null ? (
            <>
              <p className="text-xs text-muted-foreground">Profile tersimpan di router MikroTik; Harga Modal/Jual & masa aktif dipakai otomatis saat agen membuat atau memperpanjang secret. Menyimpan perubahan wajib PIN.</p>
              <div className="border border-border rounded-xl divide-y divide-border max-h-80 overflow-y-auto">
                {packages.map((p) => (
                  <div key={p.profile} className="flex items-center gap-3 px-3 py-2.5" data-testid={`ppp-profile-row-${p.profile}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm font-semibold truncate">{p.profile}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">
                        {p.rate_limit || "-"} • modal {rupiah(p.modal)} • jual {rupiah(p.jual)} • {p.expired_days ?? 30} hari
                      </div>
                    </div>
                    {p.hide_from_agents && <span className="text-[10px] font-mono text-amber-400 shrink-0" data-testid={`ppp-profile-hidden-${p.profile}`}>tersembunyi dari agen</span>}
                    {!p.on_router && <span className="text-[10px] font-mono text-amber-500 shrink-0">belum di router</span>}
                    <Button size="icon" variant="ghost" className="text-sky-400" title="Copy Profile" onClick={() => setPkgForm({ ...PPP_PROFILE_EMPTY, local_address: p.local_address || "", remote_address: p.remote_address || "", rate_limit: p.rate_limit || "", modal: p.modal, jual: p.jual, expired_days: p.expired_days ?? 30, catatan: p.catatan || "" })} data-testid={`btn-copy-ppp-profile-${p.profile}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setPkgForm({ ...PPP_PROFILE_EMPTY, name: p.profile, local_address: p.local_address || "", remote_address: p.remote_address || "", rate_limit: p.rate_limit || "", modal: p.modal, jual: p.jual, expired_days: p.expired_days ?? 30, catatan: p.catatan || "", hide_from_agents: !!p.hide_from_agents, editing: p.profile })} data-testid={`btn-edit-ppp-profile-${p.profile}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="icon" className="bg-rose-500 hover:bg-rose-600 text-white h-8 w-8" onClick={() => { setPkgDelete(p.profile); setPkgPinOpen(true); }} data-testid={`btn-del-ppp-profile-${p.profile}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {!packages.length && <div className="px-3 py-6 text-center text-xs text-muted-foreground">Belum ada profile PPP di router ini</div>}
              </div>
              <Button variant="outline" className="w-full" onClick={() => setPkgForm({ ...PPP_PROFILE_EMPTY })} data-testid="btn-add-ppp-profile">
                <Plus className="w-4 h-4 mr-1.5" /> Tambah Profile Baru
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>Nama Paket</Label><Input value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} disabled={!!pkgForm.editing} placeholder="cth: 10Mbps-bayar150.000-1/5HP" data-testid="pppf-name" /></div>
                <div className="space-y-1.5"><Label>Local Address</Label><Input value={pkgForm.local_address} onChange={(e) => setPkgForm({ ...pkgForm, local_address: e.target.value })} placeholder="cth: 172.16.4.1" data-testid="pppf-local" /></div>
                <div className="space-y-1.5"><Label>Remote Address</Label><Input value={pkgForm.remote_address} onChange={(e) => setPkgForm({ ...pkgForm, remote_address: e.target.value })} placeholder="cth: pool-pppoe" data-testid="pppf-remote" /></div>
                <div className="space-y-1.5 col-span-2"><Label>Rate Limit</Label><Input value={pkgForm.rate_limit} onChange={(e) => setPkgForm({ ...pkgForm, rate_limit: e.target.value })} placeholder="cth: 1M/2M 1.5M/4M 1M/2M 10/10 8 512k/1M" data-testid="pppf-rate" /></div>
                <div className="space-y-1.5"><Label>Harga Modal (Rp)</Label><Input type="number" min="0" value={pkgForm.modal} onChange={(e) => setPkgForm({ ...pkgForm, modal: e.target.value })} data-testid="pppf-modal" /></div>
                <div className="space-y-1.5"><Label>Harga Jual (Rp)</Label><Input type="number" min="0" value={pkgForm.jual} onChange={(e) => setPkgForm({ ...pkgForm, jual: e.target.value })} data-testid="pppf-jual" /></div>
                <div className="space-y-1.5"><Label>Expired (hari)</Label><Input type="number" min="1" value={pkgForm.expired_days} onChange={(e) => setPkgForm({ ...pkgForm, expired_days: e.target.value })} data-testid="pppf-expired" /></div>
                <div className="space-y-1.5"><Label>Catatan</Label><Input value={pkgForm.catatan} onChange={(e) => setPkgForm({ ...pkgForm, catatan: e.target.value })} data-testid="pppf-catatan" /></div>
              </div>
              {me?.role === "admin" && (
                <label className="flex items-center gap-2.5 border border-amber-500/40 rounded-lg px-3 py-2.5 cursor-pointer" data-testid="pppf-hide-wrap">
                  <input type="checkbox" className="accent-amber-500 w-4 h-4" checked={!!pkgForm.hide_from_agents} onChange={(e) => setPkgForm({ ...pkgForm, hide_from_agents: e.target.checked })} data-testid="pppf-hide" />
                  <span className="text-sm">Sembunyikan profile dari semua level agen <span className="text-xs text-muted-foreground">(khusus server / internet gratis — pelanggan profile ini tidak tampil & tidak bisa diperpanjang agen)</span></span>
                </label>
              )}
              {pkgForm.editing && (
                <label className="flex items-start gap-2 text-sm cursor-pointer rounded-md border border-sky-500/30 bg-sky-500/5 p-2.5">
                  <input type="checkbox" className="mt-0.5" checked={!!pkgForm.apply_to_secrets} onChange={(e) => setPkgForm({ ...pkgForm, apply_to_secrets: e.target.checked })} data-testid="pppf-apply-secrets" />
                  <span>Terapkan harga jual & modal ini ke <b>semua pelanggan</b> yang memakai profile ini (harga lama per pelanggan akan ditimpa)</span>
                </label>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setPkgForm(null)} data-testid="btn-cancel-ppp-profile">Batal</Button>
                <Button className="flex-1" onClick={() => setPkgPinOpen(true)} disabled={!pkgForm.name.trim() || savingPkg} data-testid="btn-save-ppp-profile">
                  {savingPkg ? "Menyimpan..." : pkgForm.editing ? "Simpan Perubahan" : "Simpan Profile"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PinConfirm open={pkgPinOpen} onOpenChange={setPkgPinOpen}
        title={pkgDelete ? `Hapus Profile ${pkgDelete}?` : "Konfirmasi Simpan Profile"}
        description={pkgDelete ? "Profile dihapus dari router MikroTik & daftar harga." : "Masukkan PIN untuk menyimpan profile & harga WiFi Rumahan."}
        confirmLabel={pkgDelete ? "Hapus" : "Simpan"} destructive={!!pkgDelete}
        onConfirm={pkgDelete ? doDeleteProfile : saveProfile} testid="pin-ppp-profile" />

      <Dialog open={!!pwTarget} onOpenChange={(o) => { if (!o) setPwTarget(null); }}>
        <DialogContent data-testid="dialog-ppp-password">
          <DialogHeader><DialogTitle>Ubah Password: {pwTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Password saat ini: <span className="font-mono font-semibold text-foreground">{pwTarget?.password}</span></p>
            <div className="space-y-1.5">
              <Label>Password Baru</Label>
              <Input value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="ketik password baru" data-testid="input-ppp-new-password" />
            </div>
            <Button className="w-full" disabled={!pwValue.trim()} onClick={() => setPinPwOpen(true)} data-testid="btn-save-ppp-password">Simpan Password Baru</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent data-testid="dialog-ppp-rename">
          <DialogHeader><DialogTitle>Ganti Nama Secret: {renameTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Nama diganti di MikroTik dan semua data terkait (harga, expired, komplain) ikut dipindah. Sesi aktif pelanggan diputus agar login ulang dengan nama baru — password tidak berubah.</p>
            <div className="space-y-1.5">
              <Label>Nama Baru</Label>
              <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value.replace(/\s/g, ""))} placeholder="nama secret baru (tanpa spasi)" data-testid="input-ppp-new-name" />
            </div>
            <Button className="w-full" disabled={!renameValue.trim() || renameValue.trim() === renameTarget?.name} onClick={() => setPinRenameOpen(true)} data-testid="btn-save-ppp-rename">Simpan Nama Baru</Button>
          </div>
        </DialogContent>
      </Dialog>

      <PinConfirm
        open={pinSetupOpen}
        onOpenChange={setPinSetupOpen}
        title={`Nonaktifkan secret ${setupItem?.name}?`}
        description={`Pelanggan akan langsung OFFLINE walau masa aktif belum habis. Alasan: "${setupForm.reason || "-"}". Masukkan PIN untuk konfirmasi.`}
        confirmLabel="Ya, Nonaktifkan"
        destructive
        onConfirm={(pin) => doSetup(null, pin)}
        testid="pin-ppp-nonactive"
      />

      <PinConfirm
        open={pinRenameOpen}
        onOpenChange={setPinRenameOpen}
        title={`Ganti nama ${renameTarget?.name} → ${renameValue}?`}
        description="Pelanggan harus mengisi username baru di modem/ONT-nya agar bisa dial ulang."
        confirmLabel="Ya, Ganti Nama"
        onConfirm={saveRename}
        testid="pin-ppp-rename"
      />

      <PinConfirm
        open={pinPwOpen}
        onOpenChange={setPinPwOpen}
        title={`Ubah password ${pwTarget?.name}?`}
        description="Password PPPoE pelanggan langsung diganti di MikroTik. Sampaikan password baru ke pelanggan agar bisa login ulang."
        confirmLabel="Ya, Ubah Password"
        onConfirm={savePassword}
        testid="pin-ppp-password"
      />

      <PinConfirm
        open={bulkDelOpen}
        onOpenChange={(o) => !bulkDeleting && setBulkDelOpen(o)}
        title={`Hapus ${selNames.size} Secret Terpilih?`}
        description={`Secret akan dihapus permanen dari router MikroTik. Untuk keamanan, sistem OTOMATIS mengunduh backup (file .txt) berisi nama, password, profile, expired & harga sebelum penghapusan — simpan file tersebut.${filtered.length === selNames.size ? " PERHATIAN: Anda memilih SEMUA secret." : ""}`}
        confirmLabel={bulkDeleting ? "Menghapus..." : `Backup & Hapus ${selNames.size} Secret`}
        destructive
        onConfirm={doBulkDelete}
        testid="pin-bulk-delete-ppp"
      />

      <PinConfirm
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title={`Hapus Secret ${deleteTarget}?`}
        description="PPP secret akan dihapus langsung dari router MikroTik dan tidak bisa dikembalikan."
        confirmLabel="Ya, Hapus Secret"
        destructive
        onConfirm={doDelete}
        testid="pin-delete-ppp"
      />

      <InvoiceDialog invoice={invoiceData} onClose={() => setInvoiceData(null)} />
      <PppComplaintDialog item={complainItem} routerId={selected} onClose={() => setComplainItem(null)} />
    </div>
  );
}
